import { useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../grid/reduxStore/Store';
import { selectBlockContent } from '../../grid/reduxStore/editorSlice';
import { useSectionData } from '../../Sections/useSectionContext';
import { useSignaturesContext } from '../../providers/SignaturesProvider';
import { useCurrentUser } from 'providers/UserProvider';
import { SigneeReassignPicker } from '../../signatures/signature-select/SigneeReassignPicker';
import type { SignatureSelectOption } from '../../signatures/signature-select';
import type { AssignedSignee, SignatureBox } from 'services/entities/SignatureTypes';
import { signeeName } from 'services/entities/SignatureTypes';
import { reassignSigneeToolTestIds } from './ReassignSigneeTool.test-ids';

interface ReassignSigneeToolProps {
  blockId: string;
}

function signeeToOption(signee: AssignedSignee): SignatureSelectOption {
  const { id, uid, firstName, lastName, email, displayableName, type, signeeType } = signee;
  return {
    id,
    uid,
    firstName,
    lastName,
    email,
    displayableName,
    type,
    signeeType: signeeType ?? type,
    label: signeeName(signee),
    value: `${id}_${type}`,
    initials: `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase(),
  };
}

function optionToSignee(option: SignatureSelectOption): AssignedSignee {
  return {
    id: option.id,
    uid: option.uid,
    firstName: option.firstName,
    lastName: option.lastName,
    email: option.email,
    displayableName: option.displayableName,
    type: option.type,
    signeeType: option.signeeType,
  };
}

export function ReassignSigneeTool({ blockId }: ReassignSigneeToolProps) {
  const { t } = useTranslation();
  const { blocksContent, sectionId } = useSectionData();
  const reduxBlockContent = useAppSelector((state) => selectBlockContent(state, sectionId, blockId));
  const contextBlockContent = blocksContent[blockId];
  const block = reduxBlockContent || contextBlockContent;
  const signature = block?.contentSignature as SignatureBox | undefined;

  const { handleSignatureReassign } = useSignaturesContext();
  const {
    data: { accountId },
  } = useCurrentUser();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!signature) return null;

  const currentSignee = signature.assignedSignee;
  const label = (currentSignee && signeeName(currentSignee, true)) || t('unassigned');

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChange = (selected: SignatureSelectOption | null | undefined) => {
    handleClose();
    const newSignee = selected ? optionToSignee(selected) : null;
    handleSignatureReassign(blockId, sectionId, currentSignee ?? null, newSignee);
  };

  const open = Boolean(anchorEl);
  const currentValue = currentSignee ? signeeToOption(currentSignee) : null;

  return (
    <>
      <Box
        data-testid={reassignSigneeToolTestIds.signeeLabel}
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '0 4px 0 8px',
          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
        }}
      >
        <Typography variant="caption" sx={{ color: 'primary.contrastText', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <ArrowDropDownIcon sx={{ color: 'primary.contrastText', fontSize: 18 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 1, width: 280 } } }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SigneeReassignPicker
          value={currentValue}
          onChange={handleChange}
          accountId={accountId}
          placeholder={t('editor.signature.search_placeholder')}
        />
      </Popover>
    </>
  );
}
